// M01 /diagnosticos: assessments of the selected institution, creation (with explicit starter initialization)
// and duplicate-for-correction (BLD-26).
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ClipboardList, Copy, Gauge, FileDown, Trash2 } from "lucide-react";
import { Alert, Button, EmptyState, ErrorState, PageHeader, Panel, Pill, SelectField, Skeleton, TextField } from "../../components/ui.tsx";
import { Modal } from "../../components/dialog.tsx";
import { GuardedLink } from "../../components/plan-shell.tsx";
import { useToast } from "../../components/toast.tsx";
import { ApiError, applyFieldErrors, get, post } from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { useGuardedNavigate } from "../../lib/guard.tsx";
import { usePageTitle } from "../../lib/nav.tsx";
import {
  STATUS_TEXT, mBase, mKey, withInstitution, type AssessmentDetail, type AssessmentListItem, type AssessmentStatus, type MaturityInstitution, type TemplateSummary,
} from "../../lib/maturity.ts";
import { MaturityLayout, ModuleNav, WithInstitution } from "./MaturityLayout.tsx";
import { formatDateBR } from "../../../shared/dates.ts";
import { isoDate, requiredText } from "../../../shared/setup.ts";
import { MATURITY_LIMITS } from "../../../shared/maturity/definition.ts";

const STATUS_TONE: Record<AssessmentStatus, "gray" | "blue" | "gold" | "green"> = {
  diagnosis_draft: "gray", diagnosis_finalized: "blue", prognosis_draft: "gold", complete: "green",
};

export function useTemplates(institutionId: string) {
  return useQuery({
    queryKey: mKey(institutionId, "templates"),
    queryFn: () => get<{ data: TemplateSummary[] }>(`${mBase(institutionId)}/templates?page_size=100`),
  });
}

export function AssessmentsPage() {
  usePageTitle("Diagnóstico institucional");
  return <WithInstitution>{(inst, all) => <AssessmentsBody institution={inst} all={all} />}</WithInstitution>;
}

function AssessmentsBody({ institution, all }: { institution: MaturityInstitution; all: MaturityInstitution[] }) {
  const q = useQuery({
    queryKey: mKey(institution.id, "assessments"),
    queryFn: () => get<{ data: AssessmentListItem[]; meta: { total: number } }>(`${mBase(institution.id)}/assessments?page_size=100`),
  });
  const templates = useTemplates(institution.id);
  const [creating, setCreating] = useState(false);
  const [duplicating, setDuplicating] = useState<AssessmentListItem | null>(null);
  const [deleting, setDeleting] = useState<AssessmentListItem | null>(null);
  const published = (templates.data?.data ?? []).flatMap((t) => t.versions.filter((v) => v.state === "published").map((v) => ({ template: t, version: v })));

  return (
    <MaturityLayout>
      <PageHeader eyebrow={institution.name} title="Diagnóstico institucional"
        subtitle="Maturidade em Gestão de Pessoas: diagnóstico de consenso (como estamos) e prognóstico (como poderemos ficar)."
        actions={<Button onClick={() => setCreating(true)} disabled={templates.isPending}>Nova avaliação</Button>} />
      <ModuleNav institution={institution} all={all} />
      {creating && <NewAssessmentDialog institution={institution} published={published} templatesLoaded={!templates.isPending} onClose={() => setCreating(false)} />}
      {duplicating && <DuplicateDialog institution={institution} source={duplicating} onClose={() => setDuplicating(null)} />}
      {deleting && <DeleteDialog institution={institution} target={deleting} onClose={() => setDeleting(null)} />}
      {q.isPending ? <Skeleton lines={4} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.data.data.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Nenhuma avaliação registrada nesta instituição."
          action={<Button onClick={() => setCreating(true)}>Nova avaliação</Button>}>
          Crie uma avaliação para registrar as respostas de consenso do grupo, quesito a quesito.
        </EmptyState>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {q.data.data.map((a) => (
            <li key={a.id}>
              <Panel as="article" className="flex h-full flex-col">
                <div className="flex flex-wrap items-center gap-2"><Pill tone={STATUS_TONE[a.status]}>{STATUS_TEXT[a.status]}</Pill></div>
                <h2 className="mt-2 text-lg font-bold">{a.name}</h2>
                <p className="text-sm">Referência: <span className="num">{formatDateBR(a.reference_date)}</span> · {a.responsible_label}</p>
                <p className="mt-1 text-xs text-muted">{a.template_name}, versão {a.revision_number}{a.linked_plan_name ? ` · Oficina: ${a.linked_plan_name}` : ""}</p>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-ctl bg-paper px-3 py-2"><dt className="eyebrow">Diagnóstico</dt><dd className="num mt-0.5">{a.diagnosis.answered} de {a.diagnosis.total}</dd></div>
                  <div className="rounded-ctl bg-paper px-3 py-2"><dt className="eyebrow">Prognóstico</dt><dd className="num mt-0.5">{a.prognosis ? `${a.prognosis.answered} de ${a.prognosis.total}` : "não iniciado"}</dd></div>
                </dl>
                <div className="mt-auto flex flex-wrap gap-2 pt-4">
                  <GuardedLink href={withInstitution(`/diagnosticos/${a.id}/avaliacao/${a.status === "prognosis_draft" ? "prognosis" : "diagnosis"}`, institution.id)}
                    className="inline-flex min-h-10 items-center gap-2 rounded-ctl bg-primary px-4 text-sm font-semibold text-white shadow-e1 hover:bg-primary-dark">
                    Abrir avaliação
                  </GuardedLink>
                  <GuardedLink href={withInstitution(`/diagnosticos/${a.id}/painel`, institution.id)} className="elevate inline-flex min-h-10 items-center gap-1.5 rounded-ctl px-3 text-sm font-semibold text-primary">
                    <Gauge className="size-4" aria-hidden />Painel
                  </GuardedLink>
                  <GuardedLink href={withInstitution(`/diagnosticos/${a.id}/relatorios`, institution.id)} className="elevate inline-flex min-h-10 items-center gap-1.5 rounded-ctl px-3 text-sm font-semibold text-primary">
                    <FileDown className="size-4" aria-hidden />Relatórios
                  </GuardedLink>
                  <Button variant="ghost" onClick={() => setDuplicating(a)}><Copy className="size-4" aria-hidden />Duplicar</Button>
                  <Button variant="dangerGhost" onClick={() => setDeleting(a)} aria-label={`Excluir ${a.name}`}><Trash2 className="size-4" aria-hidden />Excluir</Button>
                </div>
              </Panel>
            </li>
          ))}
        </ul>
      )}
    </MaturityLayout>
  );
}

const identitySchema = {
  name: requiredText(MATURITY_LIMITS.name, "Informe o nome da avaliação."),
  reference_date: isoDate("Informe a data de referência."),
  responsible_label: requiredText(MATURITY_LIMITS.label, "Informe o responsável."),
};
const createSchema = z.object({ ...identitySchema, template_version_id: z.string().min(1, "Escolha o questionário."), linked_plan_id: z.string() });

/** Today in Brasília as YYYY-MM-DD (reference dates are calendar days, not instants). */
const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Fortaleza" }).format(new Date());

function NewAssessmentDialog({ institution, published, templatesLoaded, onClose }: {
  institution: MaturityInstitution;
  published: { template: TemplateSummary; version: TemplateSummary["versions"][number] }[];
  templatesLoaded: boolean;
  onClose: () => void;
}) {
  const { user } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const go = useGuardedNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loadingStarter, setLoadingStarter] = useState(false);
  const form = useForm<z.input<typeof createSchema>>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", reference_date: today(), responsible_label: user?.name ?? "", template_version_id: published.at(-1)?.version.id ?? "", linked_plan_id: "" },
  });

  async function loadStarter() {
    setLoadingStarter(true);
    setError(null);
    try {
      const res = await post<{ data: TemplateSummary }>(`${mBase(institution.id)}/templates/from-starter`, { source_key: "gp-starter-v1" });
      await qc.invalidateQueries({ queryKey: mKey(institution.id, "templates") });
      form.setValue("template_version_id", res.data.versions.find((v) => v.state === "published")?.id ?? "");
      toast("success", "Questionário inicial carregado: 6 subdimensões, 16 categorias e 78 quesitos.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar o questionário.");
    } finally {
      setLoadingStarter(false);
    }
  }

  const submit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      const res = await post<{ data: AssessmentDetail }>(`${mBase(institution.id)}/assessments`, { ...v, linked_plan_id: v.linked_plan_id || null });
      await qc.invalidateQueries({ queryKey: mKey(institution.id) });
      toast("success", "Avaliação criada. Registre as respostas do diagnóstico.");
      go(withInstitution(`/diagnosticos/${res.data.id}/avaliacao/diagnosis`, institution.id));
    } catch (e) {
      applyFieldErrors(e, form.setError as never);
      setError(e instanceof ApiError ? e.message : "Não foi possível criar a avaliação.");
    }
  });
  const errors = form.formState.errors;
  const noModel = templatesLoaded && published.length === 0;

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Nova avaliação" description={`${institution.name} · Dimensão Gestão de Pessoas`} wide
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void submit()} loading={form.formState.isSubmitting} disabled={noModel}>Criar avaliação</Button></>}>
      <form noValidate onSubmit={submit}>
        {error && <Alert tone="danger">{error}</Alert>}
        {noModel && (
          <Alert tone="info" title="Ainda não há questionário publicado nesta instituição."
            action={<Button variant="secondary" loading={loadingStarter} onClick={() => void loadStarter()}>Carregar questionário inicial</Button>}>
            O questionário inicial de Gestão de Pessoas tem 6 subdimensões, 16 categorias e 78 quesitos. Ele é copiado para esta instituição, sem respostas.
          </Alert>
        )}
        <TextField label="Nome da avaliação" required autoFocus placeholder="Ex.: Diagnóstico de maturidade 2026" error={errors.name?.message} {...form.register("name")} />
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextField label="Data de referência" type="date" required error={errors.reference_date?.message} {...form.register("reference_date")} />
          <TextField label="Responsável (instrutor)" required hint="Nome exibido nos relatórios." error={errors.responsible_label?.message} {...form.register("responsible_label")} />
        </div>
        <SelectField label="Questionário" required error={errors.template_version_id?.message} disabled={noModel} {...form.register("template_version_id")}>
          {noModel && <option value="">Nenhum questionário publicado</option>}
          {published.map(({ template, version }) => <option key={version.id} value={version.id}>{template.name} · versão {version.revision_number} ({version.question_count} quesitos)</option>)}
        </SelectField>
        <SelectField label="Oficina vinculada (opcional)" hint="Necessária para atribuir categorias aos participantes da oficina. Não altera os cálculos." {...form.register("linked_plan_id")}>
          <option value="">Nenhuma</option>
          {institution.linkable_plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </SelectField>
      </form>
    </Modal>
  );
}

const dupSchema = z.object(identitySchema);

function DuplicateDialog({ institution, source, onClose }: { institution: MaturityInstitution; source: AssessmentListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const go = useGuardedNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.input<typeof dupSchema>>({
    resolver: zodResolver(dupSchema),
    defaultValues: { name: `${source.name} (correção)`.slice(0, MATURITY_LIMITS.name), reference_date: source.reference_date, responsible_label: source.responsible_label },
  });
  const submit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      const detail = await get<{ data: AssessmentDetail }>(`${mBase(institution.id)}/assessments/${source.id}`);
      const res = await post<{ data: AssessmentDetail }>(`${mBase(institution.id)}/assessments/${source.id}/duplicate`, { expected_version: detail.data.version, ...v });
      await qc.invalidateQueries({ queryKey: mKey(institution.id) });
      toast("success", "Nova avaliação criada com as respostas do diagnóstico original.");
      go(withInstitution(`/diagnosticos/${res.data.id}/avaliacao/diagnosis`, institution.id));
    } catch (e) {
      applyFieldErrors(e, form.setError as never);
      setError(e instanceof ApiError ? e.message : "Não foi possível duplicar a avaliação.");
    }
  });
  const errors = form.formState.errors;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Duplicar para correção"
      description="Cria uma nova avaliação em rascunho com as respostas do diagnóstico desta. A avaliação original e seus resultados ficam preservados; o prognóstico não é copiado."
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void submit()} loading={form.formState.isSubmitting}>Duplicar</Button></>}>
      <form noValidate onSubmit={submit}>
        {error && <Alert tone="danger">{error}</Alert>}
        <TextField label="Nome da nova avaliação" required error={errors.name?.message} {...form.register("name")} />
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextField label="Data de referência" type="date" required error={errors.reference_date?.message} {...form.register("reference_date")} />
          <TextField label="Responsável (instrutor)" required error={errors.responsible_label?.message} {...form.register("responsible_label")} />
        </div>
      </form>
    </Modal>
  );
}

function DeleteDialog({ institution, target, onClose }: { institution: MaturityInstitution; target: AssessmentListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const detail = await get<{ data: AssessmentDetail }>(`${mBase(institution.id)}/assessments/${target.id}`);
      await post(`${mBase(institution.id)}/assessments/${target.id}/delete`, { expected_version: detail.data.version });
      await qc.invalidateQueries({ queryKey: mKey(institution.id) });
      toast("success", "Avaliação excluída.");
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não foi possível excluir a avaliação.");
      setBusy(false);
    }
  }
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Excluir avaliação"
      description="A avaliação sai das listas, painéis e relatórios. Ela continua guardada para auditoria."
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button variant="danger" onClick={() => void confirm()} loading={busy}>Excluir</Button></>}>
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="text-sm">Excluir <strong>{target.name}</strong>{target.diagnosis.state === "finalized" ? ", inclusive o diagnóstico encerrado" : ""}?</p>
    </Modal>
  );
}
